import { useResizeObserver } from "@mantine/hooks";
import {
  useState,
  useEffect,
  useRef,
} from "react";
import { SessionResults } from "./ZFResultTabs";

type MaillageCell = {
  successes: number;
  total: number;
};

type MaillageType = MaillageCell[][];

interface ResultType {
  position: string;
  reactionTime?: string | number;
  successCount: string;
  failedCount?: number;
}

interface ResultType2 {
  position: string;
  successCount: number;
  failedCount: number;
}

const CanvasGrid = ({
  width,
  height,
  maillage,
  zvCellsInputData,
  removedRows = [],
  removedColumns = [],
}: {
  width: number;
  height: number;
  maillage: MaillageType;
  zvCellsInputData: boolean[][];
  removedRows?: number[]; // rows we want in pink
  removedColumns?: number[]; // columns we want in pink
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scaleWidth = 50; // Width of the color scale
  const squareHeight = height / 10; // Height of each square in the scale

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext("2d");
      if (context) {
        const cellHeight = height / 12;
        const cellWidth = cellHeight * (5.5 / 4); // Width based on height with ratio 5.5/4

        // Clear the canvas before drawing
        context.clearRect(0, 0, width, height);

        // Draw the color scale squares
        for (let i = 0; i < 10; i++) {
          const grayValue = Math.floor((i / 10) * 255);
          const color = `rgb(${grayValue}, ${grayValue}, ${grayValue})`;
          context.fillStyle = color;
          context.fillRect(
            0,
            height - (i + 1) * squareHeight,
            scaleWidth,
            squareHeight
          );

          // Text for each 10% slice
          context.fillStyle = "grey"; // Text contrast based on the background color
          context.font = "8px Arial";
          context.textAlign = "left";
          context.textBaseline = "middle";
          context.fillText(`${i * 10}%`, 5, height - (i + 0.5) * squareHeight);
        }

        // Draw the cells
        maillage.forEach((row, rowIndex) => {
          row.forEach((cell, colIndex) => {
            const successRate =
              cell.total > 0 ? (cell.successes / cell.total) * 100 : -1;
            const grayValue =
              successRate >= 0 ? Math.floor((successRate / 100) * 255) : 0;
              let color 
              const isRemovedRow = removedRows.includes(rowIndex);
              const isRemovedCol = removedColumns.includes(colIndex);
      
              if (isRemovedRow || isRemovedCol) {
                // Cells in removed row/col -> PINK
                color = "#006599";
              } else {
                // Otherwise, color based on cell value
                color = successRate >= 0
                  ? `rgb(${grayValue}, ${grayValue}, ${grayValue})`
                  : zvCellsInputData[rowIndex][colIndex]
                  ? "white" // Display in white if in ZV
                  : "black"; // Otherwise, display in black
              }

            

            context.fillStyle = color;
            context.fillRect(
              scaleWidth + colIndex * cellWidth,
              rowIndex * cellHeight,
              cellWidth,
              cellHeight
            );

            // Grid lines in black
            context.strokeStyle = "black";
            context.lineWidth = 1;
            context.strokeRect(
              scaleWidth + colIndex * cellWidth,
              rowIndex * cellHeight,
              cellWidth,
              cellHeight
            );

            // Text for success rates (displayed only if total > 0)
            if (isRemovedRow || isRemovedCol) {
              context.fillStyle = "#006599";
            }  else if (cell.total > 0) {
              const textColor = grayValue > 128 ? "black" : "white";
              context.fillStyle = textColor;
              context.textAlign = "center";
              context.textBaseline = "middle";
              const text = `${successRate.toFixed(0)}%`;
              context.fillText(
                text,
                scaleWidth + colIndex * cellWidth + cellWidth / 2,
                rowIndex * cellHeight + cellHeight / 2
              );
            }
          });
        });
        // Draw the central cross
        const totalGridWidth = 16 * cellWidth;
        const centerX = scaleWidth + totalGridWidth / 2;
        const centerY = height / 2;
        const crossLength = Math.min(cellWidth, cellHeight) * 0.35;

        // Set cross style (red color, separate from grid)
        context.beginPath();
        context.strokeStyle = "red";
        context.lineWidth = 2;
        context.moveTo(centerX - crossLength / 2, centerY);
        context.lineTo(centerX + crossLength / 2, centerY);
        context.stroke();

        context.beginPath();
        context.strokeStyle = "red";
        context.lineWidth = 2;
        context.moveTo(centerX, centerY - crossLength / 2);
        context.lineTo(centerX, centerY + crossLength / 2);
        context.stroke();
      }
    }
  }, [maillage, width, height, scaleWidth, squareHeight, removedRows, removedColumns, zvCellsInputData]);


  return (
    <canvas
      ref={canvasRef}
      width={scaleWidth + (16 * (height / 12) * (5.5 / 4))}
      height={height}
      style={{ cursor: "pointer", maxWidth: "100%", height: "auto" }}
    />
  );
};

const ResultsZfDisplay = 
  (
    {
      resultData,
    }: {
      resultData: SessionResults;
    },
  ) => {
    const [ref, rect] = useResizeObserver();

    const [maillage, setMaillage] = useState(
      [...Array(12)].map(() =>
        Array(16)
          .fill(null)
          .map(() => ({ successes: 0, total: 0 }))
      )
    );

    const [zvCellsInputData, setZvCellsInputData] = useState(
      [...Array(12)].map(() => Array(16).fill(false))
    );    

    // Handle changes in selectionData and resultData props
    useEffect(() => {
      if (resultData) {
        let jsonResults;

        try {
          if (resultData.resultsZF && resultData.resultsZF !== "null") {
            jsonResults = JSON.parse(resultData.resultsZF);
          } else if (
            resultData.resultsZF === null ||
            resultData.resultsZF === "null"
          ) {
            jsonResults = null;
          }

          if (jsonResults) {
            const newResultData = jsonResults.map((result: ResultType) => {
              if (result.reactionTime) {
                return {
                  position: result.position,
                  successCount: result.reactionTime !== "Trop long" ? 1 : 0,
                  failedCount: result.reactionTime === "Trop long" ? 1 : 0,
                };
              } else {
                return {
                  position: result.position,
                  successCount: parseInt(result.successCount, 10),
                  failedCount: 0, // Assume failedCount is 0 if not provided
                };
              }
            });

            const newMaillage = [...Array(12)].map(() =>
              Array(16)
                .fill(null)
                .map(() => ({ successes: 0, total: 0 }))
            );

            newResultData.forEach((result: ResultType2) => {
              const { row, col } = convertPositionToIndices(result.position);

              // Increment values of successes and total
              newMaillage[row][col].successes += result.successCount;
              newMaillage[row][col].total +=
                result.successCount + result.failedCount;
            });
            setMaillage(newMaillage);
          } else {
            // Clear the maillage and selectedCells
            setMaillage(
              [...Array(12)].map(() =>
                Array(16)
                  .fill(null)
                  .map(() => ({ successes: 0, total: 0 }))
              )
            );
          }
        } catch (error) {
          console.error("Failed to parse JSON data:", error);
        }
      }
    }, [resultData]);

    useEffect(() => {
      if (resultData && resultData.visualFieldInput) {
        try {
          const visualFieldInput = JSON.parse(resultData.visualFieldInput);
          if (visualFieldInput && visualFieldInput.ZV) {
            const newZvCells = [...Array(12)].map(() => Array(16).fill(false));
            visualFieldInput.ZV.forEach((position: string) => {
              const { row, col } = convertPositionToIndices(position);
              newZvCells[row][col] = true;
            });
            setZvCellsInputData(newZvCells);
          } else {
            // Clear zvCells if no ZV data
            setZvCellsInputData([...Array(12)].map(() => Array(16).fill(false)));
          }
        } catch (error) {
          console.error("Failed to parse visualFieldInput:", error);
        }
      }
    }, [resultData]);    

    const convertPositionToIndices = (position: string) => {
      const letter = position.charAt(0);
      const number = parseInt(position.slice(1), 10);
      const col = letter.charCodeAt(0) - 65; // Convert letter to column index (A=0, B=1, ..., O=14, P=15)
      const row = number - 1; // Convert number to row index (1=0, 2=1, ..., 12=11)
      return { row, col };
    };

    const maxWidth = rect.width > 1400 ? 1400 : rect.width;
    // Ensure canvas doesn't exceed container width
    const availableWidth = rect.width ? rect.width - 20 : 400; // 20px for padding
    const constrainedWidth = Math.min(maxWidth, availableWidth);

    return (
      <div ref={ref} style={{ width: "100%", overflow: "hidden" }}>
        <CanvasGrid
          width={constrainedWidth}
          height={constrainedWidth * 0.54}
          maillage={maillage}
          zvCellsInputData={zvCellsInputData}
          removedRows={resultData.removedRows}
          removedColumns={resultData.removedColumns}
        />
      </div>
    );
  };


export default ResultsZfDisplay;
